import { useEffect, useState, useRef, useCallback } from 'react';
import { useApi } from '../../../utils/hooks';
import { Helpers } from '../../../utils/helpers';
import { debounce } from 'lodash';
import { SortDirection } from '../../../utils/constants/enums';
import { IEmailLogEntry, IEmailLogSearchRequest } from '../../../utils/constants/interfaces';

const useEmailLogLookup = (onChange: (emailLogId: string) => void) => {
  const defaultSearchParams: IEmailLogSearchRequest = {
    recipientEmail: '',
    sortDirection: SortDirection.DESCENDING,
    sortField: 'sendDateUtc',
    pageSize: 20,
    page: 1
  };

  const api = useApi();
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [pageIsReady, setPageIsReady] = useState<boolean>(true);
  const [emailLogResults, setEmailLogResults] = useState<IEmailLogEntry[]>([]);
  const [searchParameters, setSearchParameters] =
    useState<IEmailLogSearchRequest>(defaultSearchParams);

  const search = useCallback(async (): Promise<void> => {
    // Only search if recipient email is provided
    if (Helpers.isNullOrWhiteSpace(searchParameters.recipientEmail)) {
      setEmailLogResults([]);
      setLoadingSearch(false);
      return;
    }

    setLoadingSearch(true);

    try {
      const endpoint = '/email-logs/search';
      const apiResponse = await api.post<IEmailLogEntry[]>(
        endpoint,
        JSON.stringify(searchParameters)
      );

      if (apiResponse.isSuccessStatusCode && apiResponse.response) {
        setEmailLogResults(apiResponse.response);
      } else {
        setEmailLogResults([]);
      }
    } catch (error) {
      setEmailLogResults([]);
    } finally {
      setLoadingSearch(false);
    }
  }, [api, searchParameters]);

  const resetSearch = () => {
    setSearchParameters(defaultSearchParams);
    setEmailLogResults([]);
  };

  const changeSort = (sortField: string) => {
    let sortDirection: SortDirection;
    if (searchParameters.sortField === sortField) {
      // Same field clicked - toggle direction
      sortDirection =
        (searchParameters.sortDirection as SortDirection) === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING;
    } else {
      // Different field clicked - start with descending for dates
      sortDirection = SortDirection.DESCENDING;
    }

    setSearchParameters({
      ...searchParameters,
      sortField: sortField,
      sortDirection: sortDirection
    });
  };

  const selectEmailLog = (emailLogId: string) => {
    onChange(emailLogId);
  };

  const hasSearchParametersCheck = () => {
    return !Helpers.isNullOrWhiteSpace(searchParameters.recipientEmail);
  };

  const debouncedEmailLogSearch = useRef(
    debounce((recipientEmail: string) => {
      setSearchParameters((prev) => ({ ...prev, page: 1, recipientEmail: recipientEmail }));
    }, 300)
  ).current;

  useEffect(() => {
    return () => debouncedEmailLogSearch.cancel();
  }, [debouncedEmailLogSearch]);

  useEffect(() => {
    void search();
  }, [searchParameters]); // Remove 'search' from dependencies to prevent infinite loop

  return {
    pageIsReady,
    loadingSearch,
    searchParameters,
    emailLogResults,
    debouncedEmailLogSearch,
    hasSearchParameters: hasSearchParametersCheck(),
    selectEmailLog,
    changeSort,
    resetSearch
  };
};

export default useEmailLogLookup;
